import React, { useState, useContext, useReducer, useEffect } from "react";
import moment from "moment";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";

import { dateFormat, dayFormat } from "../../tools";
import eventReducer from "./eventReducer";
import DurationMenu from "./Components/DurationMenu";
import SchedulerContext from "../schedulerContext";
import AutocompleteInput from "../../Components/AutocompleteInput";
import TimeMenu from "./Components/TimeMenu";
import useInteraction from "../../UserSurveys/useInteractions";
import { useTranslation } from "react-i18next";
import useClasses from "../../useClasses";
import { Box, Typography } from "@mui/material";
import Reminders from "./Components/Reminders";

const styles = (theme) => ({
  icon: {
    display: "flex",
    alignSelf: "flex-start",
    //paddingTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
});

function Fill({
  tempEvents,
  initEvent,
  endDay,
  handlePopover,
  handleCalenderApi,
}) {
  const classes = useClasses(styles);
  const { t } = useTranslation();
  const { eventsDispatch } = useContext(SchedulerContext);
  const [event, eventDispatch] = useReducer(eventReducer, {
    ...initEvent,
    endDay,
  });

  const { interactions } = useInteraction();

  useEffect(() => {
    eventsDispatch({
      type: "FILL_EVENTS",
      payload: { tempEvents, event },
    });
  }, [tempEvents, event]);

  const handleSave = () => handlePopover({ type: "save", payload: event });

  const handleSurveyChange = (info, newValue) => {
    if (info && newValue?.id) {
      eventDispatch({ type: "UPDATE_SURVEY", payload: newValue });
    }
  };

  const handleTimeChange = (key) => (info, value) => {
    if (info) {
      eventDispatch({
        type: "UPDATE_TIME",
        payload: { key, info, value },
      });
    }
  };

  const handleLogicChange = (index) => (info) =>
    eventDispatch({
      type: "UPDATE_LOGIC",
      payload: { value: info.target.value, index: index },
    });

  const handleStartDate = (value) => {
    handleCalenderApi({
      type: "gotoDate",
      payload: moment(value, dateFormat).valueOf(),
    });
    handlePopover({
      type: "updateDayCellAnchorEl",
      payload: moment(value, dateFormat).format(dayFormat),
    });
    eventDispatch({ type: "UPDATE_DAY", payload: { key: "start", value } });
    eventDispatch({ type: "UPDATE_DAY", payload: { key: "endDay", value } });
  };

  // Specific to Fill component
  const handleEndDate = (value) => {
    eventDispatch({ type: "UPDATE_DAY", payload: { key: "endDay", value } });
  };

  const handleAddTime = () => {
    eventDispatch({ type: "ADD_TIME" });
  };

  const handleRemoveTime = (index) => () => {
    eventDispatch({ type: "DELETE_TIME", payload: index });
  };

  return (
    <div>
      <Box
        display="flex"
        justifyContent="flex-end"
        backgroundColor="rgba(0, 0, 0, 0.1)"
      >
        <Tooltip title={t("common.actions.close")}>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => handlePopover({ type: "cancel" })}
            sx={{ padding: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box padding={2} minWidth="fit-content">
        {/** Survey selection */}
        <Box display="flex" paddingBottom={2}>
          <Box className={classes.icon} pt={1}>
            <AssignmentOutlinedIcon fontSize="small" />
          </Box>
          <div>
            <AutocompleteInput
              width={200}
              options={interactions}
              getOptionLabel={(option) => option.name}
              value={{ name: event.title, id: event.itemId }}
              onChange={handleSurveyChange}
              variant="scheduler"
              textAlign="left"
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                );
              }}
            />
          </div>
        </Box>
        {/** Start and end day */}
        <Box display="flex" paddingBottom={2}>
          <Box className={classes.icon} pt={1}>
            <CalendarTodayOutlinedIcon fontSize="small" />
          </Box>
          <div>
            <DurationMenu
              startAdornment={t("scheduler.start")}
              selectedDate={event.start}
              handleDateChange={handleStartDate}
              minDate={moment().startOf("day")}
            />
            <DurationMenu
              startAdornment={t("scheduler.end")}
              selectedDate={event.endDay}
              handleDateChange={handleEndDate}
              minDate={moment(event.start).startOf("day")}
            />
          </div>
        </Box>
        {/** Start time and end time block */}
        <Box paddingBottom={2}>
          <Box display="flex" justifyContent={"space-between"}>
            <Box display="flex" alignItems={"center"}>
              <Box className={classes.icon} pt={0.5}>
                <ScheduleOutlinedIcon fontSize="small" />
              </Box>
              <Typography variant="body2">{t("scheduler.startAndEndTimes")}</Typography>
            </Box>
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleAddTime}
              >
                {t("scheduler.addTime")}
              </Button>
            </Box>
          </Box>
          <Box pl={5}>
            {Object.keys(event).map((key) => {
              if (key.startsWith("start")) {
                const index = key.split("start")[1];
                return (
                  <div
                    key={index}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <TimeMenu
                      event={event}
                      handleLogicChange={handleLogicChange}
                      handleTimeChange={handleTimeChange}
                      index={index}
                    />
                    {index === "" ? (
                      <div></div>
                    ) : (
                      <div>
                        <Tooltip
                          title={t("common.buttons.remove")}
                          onClick={handleRemoveTime(index)}
                        >
                          <IconButton aria-label="delete_time" size="small">
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </Box>
        </Box>
        {/** Reminder block */}
        {/* <Box paddingBottom={2}>
          <Reminders
            intervals={event.reminderIntervals}
            eventDispatch={eventDispatch}
          />
        </Box> */}
        {/** Action */}
        <div
          style={{
            marginLeft: 32,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            style={{ marginLeft: 16 }}
          >
            {t("common.actions.ok")}
          </Button>
        </div>
      </Box>
    </div>
  );
}

export default Fill;
